import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUserPlus, 
  FaCalendarCheck, 
  FaCertificate, 
  FaUsers, 
  FaHandsHelping,
  FaClock,
  FaStar,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone
} from 'react-icons/fa';

const VolunteerPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    skills: '',
    interests: '',
    availability: ''
  });

  const volunteerOpportunities = [
    { 
      title: 'Tree Plantation Drives', 
      location: 'Multiple Locations', 
      time: 'Weekends, 8 AM - 12 PM',
      skills: 'Gardening, Physical Work',
      icon: '🌳'
    },
    { 
      title: 'Educational Support', 
      location: 'Community Centers', 
      time: 'Weekdays, 4 PM - 6 PM',
      skills: 'Teaching, Mentoring',
      icon: '📚'
    },
    { 
      title: 'Natural Disaster Relief', 
      location: 'Affected Areas', 
      time: 'As Needed',
      skills: 'First Aid, Logistics',
      icon: '🆘'
    },
    { 
      title: 'Elderly Care', 
      location: 'Old Age Homes', 
      time: 'Weekends, 10 AM - 2 PM',
      skills: 'Companionship, Care',
      icon: '👵'
    },
    { 
      title: 'Animal Welfare', 
      location: 'Cow Shelters', 
      time: 'Daily, Flexible Hours',
      skills: 'Animal Care, Cleaning',
      icon: '🐄'
    },
    { 
      title: 'Administrative Support', 
      location: 'Office/Remote', 
      time: 'Flexible',
      skills: 'Computers, Organization',
      icon: '💼'
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Thank you for registering as a volunteer! We will contact you soon.');
  };

  return (
    <div className="volunteer-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-orange to-primary-green py-20">
        <div className="container-lg px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Join Our Volunteer Community</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Your time and skills can create lasting impact. Join us in building a better future together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="section-padding">
        <div className="container-lg px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Registration Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center">
                  <span className="mr-2 text-primary-green"><FaUserPlus /></span> Volunteer Registration
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                        placeholder="Enter your email"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location/City *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                      placeholder="Enter your city"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Skills & Expertise
                    </label>
                    <textarea
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent"
                      rows={3}
                      placeholder="List your skills, qualifications, or areas of expertise"
                    ></textarea>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Areas of Interest *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['Tree Plantation', 'Education', 'Natural Disaster Relief', 'Elderly Care', 'Animal Welfare', 'Administrative'].map((interest) => (
                        <label key={interest} className="flex items-center">
                          <input type="checkbox" className="rounded text-primary-green focus:ring-primary-green" />
                          <span className="ml-2 text-gray-700">{interest}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Availability *
                    </label>
                    <select className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent">
                      <option value="">Select your availability</option>
                      <option value="weekends">Weekends Only</option>
                      <option value="weekdays">Weekdays Only</option>
                      <option value="flexible">Flexible Hours</option>
                      <option value="fulltime">Full-time Volunteer</option>
                    </select>
                  </div>
                  
                  <button type="submit" className="btn-primary w-full py-4 text-lg">
                    Register as Volunteer
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Volunteer Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Benefits Card */}
              <div className="bg-gradient-to-br from-primary-green/10 to-primary-blue/10 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6">Volunteer Benefits</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <span className="text-primary-green text-xl mt-1 mr-4 flex-shrink-0"><FaCertificate /></span>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">Certification</h4>
                      <p className="text-gray-700">Receive volunteer certificates and letters of recommendation</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="text-primary-orange text-xl mt-1 mr-4 flex-shrink-0"><FaStar /></span>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">Skill Development</h4>
                      <p className="text-gray-700">Gain valuable experience and develop new skills</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="text-primary-blue text-xl mt-1 mr-4 flex-shrink-0"><FaUsers /></span>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">Community</h4>
                      <p className="text-gray-700">Join a network of like-minded individuals creating positive change</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <span className="text-teal-500 text-xl mt-1 mr-4 flex-shrink-0"><FaHandsHelping /></span>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">Impact</h4>
                      <p className="text-gray-700">Make a tangible difference in people's lives and communities</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Opportunities */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6 flex items-center">
                  <span className="mr-2 text-primary-green"><FaCalendarCheck /></span> Current Opportunities
                </h3>
                
                <div className="space-y-4">
                  {volunteerOpportunities.map((opp, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-primary-green transition-colors">
                      <div className="flex items-start">
                        <div className="text-2xl mr-4">{opp.icon}</div>
                        <div className="flex-grow">
                          <h4 className="font-semibold text-gray-800">{opp.title}</h4>
                          <div className="text-sm text-gray-600 mt-2">
                            <div className="flex items-center mb-1">
                              <span className="mr-2"><FaMapMarkerAlt /></span> {opp.location}
                            </div>
                            <div className="flex items-center mb-1">
                              <span className="mr-2"><FaClock /></span> {opp.time}
                            </div>
                            <div className="flex items-center">
                              <span className="mr-2"><FaStar /></span> Skills: {opp.skills}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Volunteer Stories */}
      <section className="section-padding bg-gray-50">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Volunteer Stories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Hear from our dedicated volunteers about their experiences
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full mr-4"></div>
                <div>
                  <div className="font-bold text-gray-800">Rahul Mehta</div>
                  <div className="text-gray-600">Volunteer since 2022</div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Volunteering with Vishwa Bandhu has been transformative. The tree plantation drives not only help the environment but also bring communities together."
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full mr-4"></div>
                <div>
                  <div className="font-bold text-gray-800">Priya Sharma</div>
                  <div className="text-gray-600">Education Volunteer</div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Teaching underprivileged children has been the most rewarding experience. Seeing their progress and smiles makes every moment worthwhile."
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full mr-4"></div>
                <div>
                  <div className="font-bold text-gray-800">Amit Patel</div>
                  <div className="text-gray-600">Natural Disaster Relief Volunteer</div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Being part of natural disaster relief operations showed me the power of community. Together, we can rebuild and restore hope in challenging times."
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerPage;